import {
	Box,
	Center,
	createStyles,
	Divider,
	Flex,
	Group,
	Skeleton,
	Space,
	Stack,
	TextInput,
} from '@mantine/core';
import { showNotification } from '@mantine/notifications';

import { IconEmoji } from '@repo/common/components/IconEmoji/IconEmoji';
import {
	Banner,
	Button,
	Icon,
	IconButton,
	MultiSelect,
	Text,
	TextInputError,
	TextInputLabel,
	Title,
} from '@repo/foundations';
import { observer } from 'mobx-react-lite';
import { useCallback, useMemo } from 'react';
import { AutomationTriggerType, queryClient } from '../../../api';
import {
	useAutomation,
	useCreateAutomation,
	useDeleteAutomation,
} from '../../../api/hooks/automation';
import { automationQueryKeyFactory } from '../../../api/hooks/automation/constants';
import { useExtendedUserList } from '../../../api/hooks/user/useExtendedUserList';
import type { IUser, IUserGroup } from '../../../api/types';

import { ExpandCollapse } from '../../../components/ExpandCollapse/ExpandCollapse';
import { useCustomFilterOptionsConfig } from '../../../components/Filter/useCustomFilterOptionsConfig';
import { openModal } from '../../../components/ModalManager';

import { RichEditor } from '../../../components/RichEditor';
import { UserAvatar } from '../../../components/UserAvatar';
import { getDisplayName } from '../../../utils/userUtils';
import PolicySection from '../../PolicyPage/PolicySection';
import { getFilterOptionsForPolicy } from '../utils/policyFilterOptions';
import AutomationFiltersModal from './AutomationFiltersModal';
import { useCreatePolicyStore } from './useCreatePolicyStore';

interface CreateAutomationData {
	title: string;
	is_enabled: boolean;
	icon: string;
	trigger_type: AutomationTriggerType;
}

const useStyles = createStyles(
	(theme, { hasError }: { hasError: boolean }) => ({
		input: {
			width: '100%',
			minHeight: theme.other.space[24],
			borderColor: theme.other.getColor('border/input/default'),
			padding: `${theme.spacing['2xs']} ${theme.spacing.sm}`,
			borderRadius: theme.radius.sm,
			borderWidth: 0.5,
			borderStyle: 'solid',
			backgroundColor: theme.other.getColor('surface/input/default'),
			...(hasError && {
				borderColor: theme.other.getColor('border/critical-secondary/default'),
				backgroundColor: theme.other.getColor('surface/critical/default'),
			}),
			'&:has(.ProseMirror-focused)': {
				boxShadow: theme.shadows.sm,
			},
		},
		editor: {
			'& .ProseMirror': {
				wordBreak: 'break-word',
				fontSize: theme.fontSizes.sm,
				minHeight: theme.other.space[24],
			},
		},
		automationIcon: {
			backgroundColor: theme.other.getColor('fill/secondary/default'),
			borderRadius: theme.radius.sm,
			padding: theme.spacing['3xs'],
		},
	})
);

type UserOrUserGroup =
	| {
			value: string;
			label: string;
			user: IUser;
			group: 'User';
	  }
	| {
			value: string;
			label: string;
			userGroup: IUserGroup;
			group: 'User group';
	  };

const RemediationStack = observer(() => {
	const store = useCreatePolicyStore();
	const { classes, theme } = useStyles({ hasError: !!store.remediationError });

	const customOptionsConfig = useCustomFilterOptionsConfig();
	const { activeUsers, userGroups } = useExtendedUserList();
	const { data: automation, isLoading: automationLoading } = useAutomation({
		id: store.automationId ?? '',
		options: {
			enabled: !!store.automationId,
		},
	});
	const { mutateAsync: apiCreateAutomation, isPending: creatingAutomation } =
		useCreateAutomation({
			invalidationKeys: [automationQueryKeyFactory.allLists()],
		});
	const { mutateAsync: deleteAutomation } = useDeleteAutomation({
		options: {
			onSuccess: async () => {
				await queryClient.invalidateQueries({
					queryKey: automationQueryKeyFactory.allLists(),
				});
			},
		},
	});

	const options: UserOrUserGroup[] = useMemo(
		() => [
			...(activeUsers?.map((u) => ({
				value: u.id,
				label: getDisplayName(u),
				user: u,
				group: 'User' as const,
			})) || []),
			...(userGroups?.map((g) => ({
				value: g.id,
				label: g.name,
				userGroup: g,
				group: 'User group' as const,
			})) || []),
		],
		[activeUsers, userGroups]
	);

	const handleRemediationGuidelinesChange = useCallback(
		(value: string | undefined) => {
			store.setRemediationGuidelines(value ?? '');
		},
		[store]
	);

	const renderIcon = useCallback((item: UserOrUserGroup) => {
		if (item.group === 'User') {
			return (
				<Center p={2}>
					<UserAvatar user={item.user} size="xxs" />
				</Center>
			);
		}
		return (
			item.userGroup.icon && <IconEmoji value={item.userGroup.icon} size="md" />
		);
	}, []);

	const handleCreateAutomation = useCallback(async () => {
		const policyName = store.name || 'Untitled';
		const automationTitle = `Remediate ${policyName}`;

		try {
			const automationData: CreateAutomationData = {
				title: automationTitle,
				is_enabled: false,
				icon: '⚡️',
				trigger_type: AutomationTriggerType.SCHEDULE,
			};

			const newAutomation = await apiCreateAutomation({
				data: automationData,
			});

			store.setCreatedAutomationId(newAutomation.id);
			store.setAutomationName(automationTitle);
		} catch (error) {
			showNotification({
				message: `Failed to create automation: ${error}`,
				color: 'red',
			});
		}
	}, [store, apiCreateAutomation]);

	const handleDeleteAutomation = useCallback(async () => {
		if (automation?.id) {
			await deleteAutomation({ id: automation.id });
			store.setAutomationId(undefined);
			store.setAutomationName('');
		}
	}, [automation?.id, deleteAutomation, store]);

	const handleSeeFilters = useCallback(() => {
		const filterOptions = getFilterOptionsForPolicy(
			customOptionsConfig,
			!store.dqsEnabled
		);

		openModal({
			modalId: 'automation-filters',
			variant: 'default',
			title: 'Automation filters',
			children: (
				<AutomationFiltersModal
					policyRules={store.policyRules}
					filterOptions={filterOptions}
				/>
			),
		});
	}, [customOptionsConfig, store.dqsEnabled, store.policyRules]);

	const renderAutomationContent = () => {
		if (automationLoading && store.automationId) {
			return (
				<Flex align="center" justify="space-between" w="100%">
					<Group>
						<Skeleton height={36} width={36} radius="md" />
						<Stack spacing={4}>
							<Skeleton height={18} width={180} radius="sm" />
							<Skeleton height={14} width={220} radius="sm" />
						</Stack>
					</Group>
					<Skeleton height={24} width={42} radius="xl" />
				</Flex>
			);
		}

		const hasAutomation = store.automationId && automation;

		return (
			<>
				<Group position="apart">
					<Group>
						<Box className={classes.automationIcon}>
							<Icon name="bolt" />
						</Box>
						<Stack spacing={0}>
							<Text size="sm" weight="semibold" children="Automation" />
							<Text
								size="xs"
								color="text/secondary/default"
								children="Enable automations to take action on resources that match this policy."
							/>
						</Stack>
					</Group>
					{hasAutomation ? (
						<IconButton
							iconName="x"
							onClick={handleDeleteAutomation}
							variant="tertiary"
							tooltip="Remove automation"
						/>
					) : (
						<IconButton
							iconName="plus"
							onClick={handleCreateAutomation}
							loading={creatingAutomation}
							variant="tertiary"
							tooltip="Create automation"
						/>
					)}
				</Group>

				{hasAutomation && (
					<Stack>
						<Space h="xs" />
						<Divider />
						<TextInput
							size="xs"
							label="Automation name"
							value={store.automationName}
							onChange={(e) => store.setAutomationName(e.target.value)}
						/>
						<Divider />
						<Group position="apart">
							<Text size="xs">Filters will match policy conditions</Text>
							<Button onClick={handleSeeFilters}>See filters</Button>
						</Group>
						<Divider />
						<Banner
							inCard
							tone="info"
							message="Automation will run daily unless paused."
						/>
					</Stack>
				)}
			</>
		);
	};

	return (
		<Stack p={0} spacing="md">
			<Title size="md">Remediation</Title>
			<PolicySection
				content={
					<Stack spacing="md">
						<Stack spacing={theme.spacing['3xs']}>
							<TextInputLabel label="Remediation guidelines" inputId="" />
							<Box className={classes.input}>
								<ExpandCollapse maxHeight={200} enabled>
									<RichEditor
										emptyDocPlaceholder="Add remediation guidelines..."
										readOnly={false}
										initialValue={store.remediationGuidelines}
										onChangeCallback={handleRemediationGuidelinesChange}
										disableTopGap
										limited
										autoFocus={false}
										dataTestId="remediation-guidelines"
										className={classes.editor}
									/>
								</ExpandCollapse>
							</Box>
							<Text size="sm" color="text/secondary/default">
								Describe the steps or actions required to resolve a policy
								violation and ensure compliance is restored.
							</Text>
							{store.remediationError && (
								<TextInputError error={store.remediationError} />
							)}
						</Stack>
						<MultiSelect<UserOrUserGroup>
							label="Owner"
							placeholder="Select"
							data={options}
							value={[...store.owners, ...store.ownersGroups]}
							setValue={(values) => {
								store.setOwners(
									values.filter(
										(v) => options.find((o) => o.value === v)?.group === 'User'
									)
								);
								store.setOwnersGroups(
									values.filter(
										(v) =>
											options.find((o) => o.value === v)?.group === 'User group'
									)
								);
							}}
							renderIcon={renderIcon}
							error={store.ownersError}
							dropdownPosition="flip"
						/>
						<MultiSelect
							label="Subscribers"
							placeholder="Select"
							data={options}
							value={[...store.subscribers, ...store.subscribersGroups]}
							setValue={(values) => {
								store.setSubscribers(
									values.filter(
										(v) => options.find((o) => o.value === v)?.group === 'User'
									)
								);
								store.setSubscribersGroups(
									values.filter(
										(v) =>
											options.find((o) => o.value === v)?.group === 'User group'
									)
								);
							}}
							renderIcon={renderIcon}
							error={store.subscribersError}
							dropdownPosition="flip"
						/>
					</Stack>
				}
			/>
			<PolicySection content={<Box>{renderAutomationContent()}</Box>} />
		</Stack>
	);
});

RemediationStack.displayName = 'RemediationStack';
export default RemediationStack;
